import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccessToken } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { Loader } from '../../../components';

const verdictColors = {
    'Safe': 'bg-green-100 text-green-800',
    'Tight': 'bg-yellow-100 text-yellow-800',
    'Risky': 'bg-red-100 text-red-800',
    'Pass': 'bg-green-100 text-green-800',
    'Fail': 'bg-red-100 text-red-800',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const SectionHeader = ({ label, badge }) => (
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{label}</h3>
        {badge}
    </div>
);

const CheckRow = ({ label, name, value, options, onChange }) => (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 hover:bg-gray-50/40 transition-colors">
        <span className="text-sm text-gray-700 w-3/5">{label}</span>
        <select
            name={name}
            value={value || ''}
            onChange={onChange}
            className="w-2/5 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        >
            <option value="">—</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const NumberInput = ({ label, name, value, onChange, prefix }) => (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <span className="text-sm text-gray-700 w-3/5">{label}</span>
        <div className="w-2/5 flex items-center gap-1">
            {prefix && <span className="text-sm text-gray-400">{prefix}</span>}
            <input
                type="number"
                name={name}
                value={value ?? ''}
                onChange={onChange}
                min={0}
                step="0.01"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
        </div>
    </div>
);

const defaultMonths = ['Month 1', 'Month 2', 'Month 3'];

const defaultMonthlySummary = {
    salary: [0, 0, 0],
    otherIncome: [0, 0, 0],
    bills: [0, 0, 0],
    loanPayments: [0, 0, 0],
    expenses: [0, 0, 0],
    savings: [0, 0, 0],
    gambling: [0, 0, 0],
    endingBalance: [0, 0, 0]
};

const defaultRedFlags = {
    gamblingAbove5Pct: '',
    microcreditRepayments: '',
    undeclaredLoanRepayments: '',
    cashWithdrawalsAbove30Pct: '',
    bouncedPayments: '',
    highIncomeVolatility: '',
    hiddenDebt: '',
    redFlagNotes: ''
};

const defaultStandingOrder = {
    indicativeRepayment: 0,
    avgEndingBalance: 0,
    minEndingBalance: 0,
    monthsBelowRepayment: 0,
    standingOrderObtained: '',
    verdict: ''
};

const categoryLabels = [
    { key: 'salary', label: 'Salary credit (Regular Income)' },
    { key: 'otherIncome', label: 'Other income' },
    { key: 'bills', label: 'Bills paid' },
    { key: 'loanPayments', label: 'Loan repayments (existing)' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'savings', label: 'Savings / investment contribution' },
    { key: 'gambling', label: 'Gambling' },
    { key: 'endingBalance', label: 'Ending balance' }
];

const redFlagLabels = [
    { key: 'gamblingAbove5Pct', label: 'Gambling > 5% of income' },
    { key: 'microcreditRepayments', label: 'Microcredit lender repayments detected' },
    { key: 'undeclaredLoanRepayments', label: 'Loan repayments not declared by customer' },
    { key: 'cashWithdrawalsAbove30Pct', label: 'Cash withdrawals > 30% of income' },
    { key: 'bouncedPayments', label: 'Multiple bounced payments / NSF fees' },
    { key: 'highIncomeVolatility', label: 'Income volatility > 0.60' },
    { key: 'hiddenDebt', label: 'Hidden debt (loans not on credit bureau)' }
];

function computeAvg(arr) {
    if (!arr || arr.length === 0) return 0;
    const nums = arr.filter(v => typeof v === 'number' && !isNaN(v));
    if (nums.length === 0) return 0;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function computeVolatility(arr) {
    if (!arr || arr.length < 2) return 0;
    const nums = arr.filter(v => typeof v === 'number' && !isNaN(v));
    if (nums.length < 2) return 0;
    const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
    if (mean === 0) return 0;
    const variance = nums.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / nums.length;
    return Math.round((Math.sqrt(variance) / mean) * 100) / 100;
}

function BankAnalysisIndex({ loanId, loanType }) {
    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [identityCheck, setIdentityCheck] = useState({ nameMatch: '', addressMatch: '', employerMatch: '' });
    const [months, setMonths] = useState([...defaultMonths]);
    const [monthlySummary, setMonthlySummary] = useState({ ...defaultMonthlySummary });
    const [metrics, setMetrics] = useState({ avgSalary: 0, avgLoanRepayments: 0, avgGambling: 0, incomeVolatility: 0, salaryMatchesPayslip: '' });
    const [redFlags, setRedFlags] = useState({ ...defaultRedFlags });
    const [standingOrder, setStandingOrder] = useState({ ...defaultStandingOrder });

    const fetchAnalysis = useCallback(async () => {
        try {
            const res = await fetchRequests(`/loans/staff/bank-analysis?loanId=${loanId}`, "GET", authStaff(accessToken), {}, {});
            if (res?.analysis) {
                const a = res.analysis;
                setIdentityCheck(a.identityCheck || { nameMatch: '', addressMatch: '', employerMatch: '' });
                if (a.months?.length) setMonths(a.months);
                if (a.monthlySummary) setMonthlySummary(a.monthlySummary);
                if (a.metrics) setMetrics(a.metrics);
                if (a.redFlags) setRedFlags(a.redFlags);
                if (a.standingOrder) setStandingOrder(a.standingOrder);
            }
        } catch (err) {
            // no existing analysis
        } finally {
            setLoading(false);
        }
    }, [loanId, accessToken]);

    useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

    // Recompute metrics whenever monthly summary changes
    useEffect(() => {
        const avgSalary = Math.round(computeAvg(monthlySummary.salary) * 100) / 100;
        const avgLoanRepayments = Math.round(computeAvg(monthlySummary.loanPayments) * 100) / 100;
        const avgGambling = Math.round(computeAvg(monthlySummary.gambling) * 100) / 100;
        const incomeVolatility = computeVolatility(monthlySummary.salary);

        setMetrics(prev => ({ ...prev, avgSalary, avgLoanRepayments, avgGambling, incomeVolatility }));

        // Also update standing order derived fields
        const avgEndingBalance = Math.round(computeAvg(monthlySummary.endingBalance) * 100) / 100;
        const balances = monthlySummary.endingBalance?.filter(v => typeof v === 'number' && !isNaN(v)) || [];
        const minEndingBalance = balances.length ? Math.min(...balances) : 0;

        setStandingOrder(prev => ({ ...prev, avgEndingBalance, minEndingBalance }));
    }, [monthlySummary]);

    const updateIdentity = (e) => {
        const { name, value } = e.target;
        setIdentityCheck(prev => ({ ...prev, [name]: value }));
    };

    const updateMonth = (index, value) => {
        setMonths(prev => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
    };

    const updateMonthlySummaryCell = (category, index, value) => {
        setMonthlySummary(prev => {
            const updated = { ...prev };
            updated[category] = [...(prev[category] || [0, 0, 0])];
            updated[category][index] = parseFloat(value) || 0;
            return updated;
        });
    };

    const updateRedFlag = (e) => {
        const { name, value } = e.target;
        setRedFlags(prev => ({ ...prev, [name]: value }));
    };

    const updateStandingOrder = (e) => {
        const { name, value } = e.target;
        setStandingOrder(prev => ({ ...prev, [name]: name === 'standingOrderObtained' ? value : (parseFloat(value) || 0) }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetchRequests('/loans/staff/bank-analysis', 'POST', authStaff(accessToken), {}, {
                loanId, loanType, identityCheck, months, monthlySummary, metrics, redFlags, standingOrder
            });
            if (res?.err) {
                dispatch(show({ type: 'error', state: true, message: res.err }));
            } else if (res?.analysis) {
                const a = res.analysis;
                if (a.metrics) setMetrics(a.metrics);
                if (a.standingOrder) setStandingOrder(a.standingOrder);
                dispatch(show({ type: 'success', state: true, message: 'Bank analysis saved' }));
            }
        } catch (err) {
            dispatch(show({ type: 'error', state: true, message: 'Failed to save bank analysis' }));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-10">
                <Loader color="text-gray-500" size="w-5 h-5" />
            </div>
        );
    }

    const redFlagCount = redFlagLabels.filter(r => redFlags[r.key] === 'Yes').length;

    return (
        <div className="space-y-5 py-4">
            {/* Section A — Identity Check */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section A — Identity Check" />
                <p className="px-4 py-2 text-xs text-gray-400">Cross-check bank statement against other documents</p>
                <CheckRow label="Borrower name matches" name="nameMatch" value={identityCheck.nameMatch} options={['Pass', 'Fail']} onChange={updateIdentity} />
                <CheckRow label="Address matches" name="addressMatch" value={identityCheck.addressMatch} options={['Pass', 'Fail']} onChange={updateIdentity} />
                <CheckRow label="Employer (salary credit source) matches" name="employerMatch" value={identityCheck.employerMatch} options={['Pass', 'Fail']} onChange={updateIdentity} />
            </div>

            {/* Section B — 3-Month Income Summary */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section B — 3-Month Income Summary" />
                <p className="px-4 py-2 text-xs text-gray-400">Enter monthly figures from the bank statement. Averages are auto-calculated.</p>

                {/* Month name inputs */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-gray-600 font-medium w-2/5">Category</th>
                                {months.map((m, i) => (
                                    <th key={i} className="px-2 py-2.5 w-[15%]">
                                        <input
                                            type="text"
                                            value={m}
                                            onChange={(e) => updateMonth(i, e.target.value)}
                                            className="w-full text-sm text-center border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 font-medium"
                                            placeholder={`Month ${i + 1}`}
                                        />
                                    </th>
                                ))}
                                <th className="px-2 py-2.5 text-gray-500 font-medium w-[15%]">3-Mo Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categoryLabels.map(({ key, label }) => (
                                <tr key={key} className={`border-b border-gray-50 hover:bg-gray-50/40 ${key === 'endingBalance' ? 'bg-blue-50/30' : ''}`}>
                                    <td className="px-4 py-2 text-gray-700">{label}</td>
                                    {[0, 1, 2].map(i => (
                                        <td key={i} className="px-2 py-1.5">
                                            <input
                                                type="number"
                                                value={monthlySummary[key]?.[i] ?? 0}
                                                onChange={(e) => updateMonthlySummaryCell(key, i, e.target.value)}
                                                min={0}
                                                step="0.01"
                                                className="w-full text-sm text-right border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                            />
                                        </td>
                                    ))}
                                    <td className="px-2 py-2 text-right text-gray-500 font-medium tabular-nums">
                                        {computeAvg(monthlySummary[key] || []).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Section C — Key Metrics (auto-derived) */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader label="Section C — Key Metrics" badge={<span className="text-xs text-gray-400">Auto-calculated</span>} />
                <div className="divide-y divide-gray-100">
                    <div className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm text-gray-700">Avg monthly salary credit (GHS)</span>
                        <span className="text-sm font-medium text-gray-900 tabular-nums">{metrics.avgSalary?.toLocaleString('en-GH', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm text-gray-700">Avg monthly loan repayments (GHS)</span>
                        <span className="text-sm font-medium text-gray-900 tabular-nums">{metrics.avgLoanRepayments?.toLocaleString('en-GH', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm text-gray-700">Avg monthly gambling (GHS)</span>
                        <span className="text-sm font-medium text-gray-900 tabular-nums">{metrics.avgGambling?.toLocaleString('en-GH', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm text-gray-700">Income volatility (CV)</span>
                        <span className={`text-sm font-medium tabular-nums ${metrics.incomeVolatility > 0.6 ? 'text-red-600' : metrics.incomeVolatility > 0.3 ? 'text-yellow-600' : 'text-gray-900'}`}>
                            {metrics.incomeVolatility?.toFixed(2)}
                            {metrics.incomeVolatility > 0.6 && <span className="ml-1 text-xs text-red-500">High</span>}
                        </span>
                    </div>
                    <CheckRow label="Salary matches payslip (≤10% variance)?" name="salaryMatchesPayslip" value={metrics.salaryMatchesPayslip} options={['Yes', 'No']} onChange={(e) => setMetrics(prev => ({ ...prev, salaryMatchesPayslip: e.target.value }))} />
                </div>
            </div>

            {/* Section D — Red Flags */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                    label="Section D — Red Flags"
                    badge={redFlagCount > 0
                        ? <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">{redFlagCount} flag{redFlagCount !== 1 ? 's' : ''}</span>
                        : null
                    }
                />
                {redFlagLabels.map(({ key, label }) => (
                    <CheckRow key={key} label={label} name={key} value={redFlags[key]} options={['Yes', 'No']} onChange={updateRedFlag} />
                ))}
                <div className="px-4 py-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Red Flag Notes</label>
                    <textarea
                        name="redFlagNotes"
                        value={redFlags.redFlagNotes || ''}
                        onChange={updateRedFlag}
                        rows={2}
                        className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                        placeholder="Details on any flagged items..."
                    />
                </div>
            </div>

            {/* Section E — Standing Order Viability */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                    label="Section E — Standing Order Viability"
                    badge={standingOrder.verdict ? <VerdictBadge verdict={standingOrder.verdict} /> : null}
                />
                <p className="px-4 py-2 text-xs text-gray-400">For unsecured loans, standing order is the PRIMARY repayment protection</p>
                <NumberInput label="Indicative monthly repayment (GHS)" name="indicativeRepayment" value={standingOrder.indicativeRepayment} onChange={updateStandingOrder} prefix="GHS" />
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700">Avg 3-month ending balance (GHS)</span>
                    <span className="text-sm font-medium text-gray-900 tabular-nums">{standingOrder.avgEndingBalance?.toLocaleString('en-GH', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-700">Min monthly ending balance (GHS)</span>
                    <span className="text-sm font-medium text-gray-900 tabular-nums">{standingOrder.minEndingBalance?.toLocaleString('en-GH', { minimumFractionDigits: 2 })}</span>
                </div>
                <NumberInput label="Months where balance < repayment" name="monthsBelowRepayment" value={standingOrder.monthsBelowRepayment} onChange={updateStandingOrder} />
                <CheckRow label="Standing order obtained on primary account?" name="standingOrderObtained" value={standingOrder.standingOrderObtained} options={['Yes', 'No']} onChange={updateStandingOrder} />
            </div>

            {/* Save Button */}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#162d56] transition-colors disabled:opacity-50"
                >
                    {saving && <Loader color="text-white" size="w-4 h-4" />}
                    {saving ? 'Saving...' : 'Save Bank Analysis'}
                </button>
                {standingOrder.verdict && (
                    <span className="text-sm text-gray-500">
                        Standing Order: <VerdictBadge verdict={standingOrder.verdict} />
                    </span>
                )}
            </div>
        </div>
    );
}

export default BankAnalysisIndex;
